import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosInstance';

const fmt  = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtK = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return '₹ ' + (v / 1e7).toFixed(1) + 'Cr';
  if (v >= 1e5) return '₹ ' + (v / 1e5).toFixed(1) + 'L';
  if (v >= 1e3) return '₹ ' + (v / 1e3).toFixed(1) + 'K';
  return fmt(v);
};
const fmtDate = (s) => {
  if (!s) return '—';
  return new Date(s + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

/* ── Reusable stat card ─────────────────────────────────────────────────── */
function StatCard({ icon, label, value, sub, subColor = '#64748b', accent = '#0a1f5c', bg = '#e0e7ff' }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: '18px 20px',
      boxShadow: '0 1px 4px rgba(0,0,0,.07)', border: '1px solid #e2e8f0',
      display: 'flex', gap: 14, alignItems: 'flex-start', flex: 1, minWidth: 170,
    }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <i className={icon} style={{ fontSize: 18, color: accent }}></i>
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: .4 }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', lineHeight: 1.2, marginTop: 2 }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: subColor, marginTop: 3 }}>{sub}</div>}
      </div>
    </div>
  );
}

/* ── Bar chart (CSS-based) ──────────────────────────────────────────────── */
function BarChart({ data, height = 140 }) {
  const maxVal = Math.max(...data.map(d => Math.max(d.revenue || 0, 1)));
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height }}>
      {data.map((d, i) => {
        const pct = Math.round(((d.revenue || 0) / maxVal) * 100);
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, height: '100%', justifyContent: 'flex-end' }}>
            <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{d.revenue > 0 ? fmtK(d.revenue) : ''}</div>
            <div style={{ width: '100%', position: 'relative', height: `${Math.max(pct, 3)}%`, borderRadius: '5px 5px 0 0' }}>
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,#2563eb,#0a1f5c)', borderRadius: '5px 5px 0 0', opacity: i === data.length - 1 ? 1 : 0.55 }}></div>
              {d.collected > 0 && (
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: `${Math.round((d.collected / d.revenue) * 100)}%`, background: '#16a34a', borderRadius: i === data.length - 1 ? '0 0 0 0' : '0', opacity: 0.9 }}></div>
              )}
            </div>
            <div style={{ fontSize: 9, color: '#94a3b8', whiteSpace: 'nowrap', fontWeight: 600 }}>{d.label.split(' ')[0]}</div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Funnel bar ─────────────────────────────────────────────────────────── */
function FunnelRow({ label, count, pct, color }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>{count.toLocaleString('en-IN')} <span style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}>({pct}%)</span></span>
      </div>
      <div style={{ height: 8, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 99, transition: 'width .6s ease' }}></div>
      </div>
    </div>
  );
}

/* ── Status donut (CSS trick) ───────────────────────────────────────────── */
function StatusDonut({ paid, partial, unpaid }) {
  const total = paid + partial + unpaid || 1;
  const segments = [
    { label: 'Paid',    count: paid,    color: '#16a34a', pct: Math.round((paid / total) * 100) },
    { label: 'Partial', count: partial, color: '#f59e0b', pct: Math.round((partial / total) * 100) },
    { label: 'Unpaid',  count: unpaid,  color: '#ef4444', pct: Math.round((unpaid / total) * 100) },
  ];
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {segments.map(s => (
        <div key={s.label} style={{ flex: 1, minWidth: 80, background: '#f8fafc', borderRadius: 10, padding: '10px 12px', border: `2px solid ${s.color}20` }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.count}</div>
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{s.label}</div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>{s.pct}%</div>
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════════════ */
export default function SalesDashboard() {
  const navigate  = useNavigate();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get('/dashboard/sales');
      setData(res.data.data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="page-wrapper">
      <div className="content container-fluid" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
        <div style={{ textAlign: 'center' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: 36, color: '#2563eb', display: 'block', marginBottom: 14 }}></i>
          <div style={{ color: '#64748b', fontWeight: 600 }}>Loading dashboard…</div>
        </div>
      </div>
    </div>
  );

  if (error) return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="alert alert-danger"><i className="fas fa-exclamation-circle me-2"></i>{error}</div>
      </div>
    </div>
  );

  const { overview, thisMonth, invoiceStatus, monthly, recentInvoices, outstandingInvoices, topCustomers } = data;

  const maxRevenue = Math.max(...(topCustomers || []).map(c => c.revenue), 1);

  return (
    <div className="page-wrapper" style={{ background: '#f1f5f9' }}>
      <div className="content container-fluid" style={{ padding: '18px 24px' }}>

        {/* ── Page Header ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 46, height: 46, borderRadius: 14, background: 'linear-gradient(135deg,#0a1f5c,#2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-chart-line" style={{ color: '#fff', fontSize: 20 }}></i>
            </div>
            <div>
              <h4 style={{ margin: 0, fontWeight: 900, color: '#0f172a', fontSize: 22 }}>Sales Dashboard</h4>
              <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>Enterprise overview · all-time + this month</p>
            </div>
          </div>
          <button onClick={load} style={{ background: '#fff', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 10, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7 }}>
            <i className="fas fa-sync-alt"></i> Refresh
          </button>
        </div>

        {/* ── Row 1: Key metrics ── */}
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 18 }}>
          <StatCard icon="fas fa-users"              label="Total Leads"      value={overview.totalLeads.toLocaleString('en-IN')}
            sub={`+${thisMonth.leads} this month`}   accent="#0a1f5c" bg="#e0e7ff" />
          <StatCard icon="fas fa-building"           label="Accounts"         value={overview.totalAccounts.toLocaleString('en-IN')}
            sub={`${overview.convertedLeads} converted leads`} accent="#065f46" bg="#d1fae5" />
          <StatCard icon="fas fa-file-alt"           label="Quotations"       value={overview.totalQuotations.toLocaleString('en-IN')}
            sub={`+${thisMonth.quotations} this month ${thisMonth.revGrowth >= 0 ? '▲' : '▼'}${Math.abs(thisMonth.revGrowth)}%`}
            subColor={thisMonth.revGrowth >= 0 ? '#16a34a' : '#ef4444'} accent="#7c3aed" bg="#ede9fe" />
          <StatCard icon="fas fa-file-invoice-dollar" label="Invoices"        value={overview.totalInvoices.toLocaleString('en-IN')}
            sub={`+${thisMonth.invoices} this month ${thisMonth.invGrowth >= 0 ? '▲' : '▼'}${Math.abs(thisMonth.invGrowth)}%`}
            subColor={thisMonth.invGrowth >= 0 ? '#16a34a' : '#ef4444'} accent="#b45309" bg="#fef3c7" />
          <StatCard icon="fas fa-rupee-sign"         label="Total Revenue"    value={fmtK(overview.totalRevenue)}
            sub="Sum of all invoices"                accent="#b91c1c" bg="#fee2e2" />
          <StatCard icon="fas fa-hand-holding-usd"   label="Collected"        value={fmtK(overview.totalCollected)}
            sub={`Advance + Payments · Outstanding: ${fmtK(overview.outstanding)}`} subColor={overview.outstanding > 0 ? '#ef4444' : '#16a34a'} accent="#0d9488" bg="#ccfbf1" />
        </div>

        {/* ── Row 2: Conversion + Chart ── */}
        <div style={{ display: 'flex', gap: 18, marginBottom: 18, flexWrap: 'wrap' }}>

          {/* Conversion Funnel */}
          <div style={{ flex: 1, minWidth: 280, background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,.06)', border: '1px solid #e2e8f0' }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-filter" style={{ color: '#2563eb', fontSize: 13 }}></i> Sales Funnel
            </div>
            <FunnelRow label="Total Leads"     count={overview.totalLeads}         pct={100}                                                             color="#0a1f5c" />
            <FunnelRow label="Leads → Quote"   count={overview.leadsWithQuotation} pct={overview.conversionRate}                                        color="#2563eb" />
            <FunnelRow label="Accounts"        count={overview.totalAccounts}      pct={Math.round((overview.totalAccounts / Math.max(overview.totalLeads,1)) * 100)}  color="#16a34a" />
            <FunnelRow label="Invoices Raised" count={overview.totalInvoices}      pct={Math.round((overview.totalInvoices / Math.max(overview.totalLeads,1)) * 100)}  color="#f59e0b" />

            <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>Lead → Quotation Conversion</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, height: 10, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${overview.conversionRate}%`, background: 'linear-gradient(90deg,#0a1f5c,#2563eb)', borderRadius: 99 }}></div>
                </div>
                <span style={{ fontSize: 15, fontWeight: 800, color: '#0a1f5c' }}>{overview.conversionRate}%</span>
              </div>
            </div>
          </div>

          {/* Revenue Trend */}
          <div style={{ flex: 2, minWidth: 320, background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,.06)', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="fas fa-chart-bar" style={{ color: '#2563eb', fontSize: 13 }}></i> Monthly Revenue (Last 6 Months)
              </div>
              <div style={{ display: 'flex', gap: 16, fontSize: 11 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 10, height: 10, background: 'linear-gradient(#2563eb,#0a1f5c)', borderRadius: 2, display: 'inline-block' }}></span>Invoiced
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 10, height: 10, background: '#16a34a', borderRadius: 2, display: 'inline-block' }}></span>Collected
                </span>
              </div>
            </div>
            <BarChart data={monthly} height={160} />
            <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: 14, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
              {monthly.slice(-3).map((m, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{m.label}</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>{fmtK(m.revenue)}</div>
                  <div style={{ fontSize: 11, color: '#16a34a' }}>↑ {fmtK(m.collected)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Invoice Status */}
          <div style={{ flex: 1, minWidth: 200, background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,.06)', border: '1px solid #e2e8f0' }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-circle-notch" style={{ color: '#2563eb', fontSize: 13 }}></i> Invoice Status
            </div>
            <StatusDonut paid={invoiceStatus.paid} partial={invoiceStatus.partial} unpaid={invoiceStatus.unpaid} />
            <div style={{ marginTop: 14, padding: '10px 14px', background: '#f8fafc', borderRadius: 10 }}>
              <div style={{ fontSize: 12, color: '#64748b' }}>Collection Rate</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginTop: 2 }}>
                {overview.totalRevenue > 0 ? Math.round((overview.totalCollected / overview.totalRevenue) * 100) : 0}%
              </div>
              <div style={{ height: 6, background: '#e2e8f0', borderRadius: 99, marginTop: 6, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${overview.totalRevenue > 0 ? Math.round((overview.totalCollected / overview.totalRevenue) * 100) : 0}%`, background: '#16a34a', borderRadius: 99 }}></div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Row 3: Tables ── */}
        <div style={{ display: 'flex', gap: 18, marginBottom: 18, flexWrap: 'wrap' }}>

          {/* Recent Invoices */}
          <div style={{ flex: 1, minWidth: 320, background: '#fff', borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,.06)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>
                <i className="fas fa-file-invoice-dollar me-2" style={{ color: '#2563eb' }}></i>Recent Invoices
              </span>
              <button onClick={() => navigate('/quotation')}
                style={{ background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 7, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                View all
              </button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Invoice No.','Customer','Amount','Date'].map(h => (
                      <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(recentInvoices || []).length === 0 ? (
                    <tr><td colSpan={4} style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>No invoices yet</td></tr>
                  ) : (recentInvoices || []).map(inv => (
                    <tr key={inv.id} style={{ borderTop: '1px solid #f1f5f9' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ background: '#fee2e2', color: '#7c2d12', borderRadius: 5, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>{inv.number}</span>
                      </td>
                      <td style={{ padding: '10px 14px', color: '#374151', fontWeight: 600, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.customer_name || '—'}</td>
                      <td style={{ padding: '10px 14px', fontWeight: 700, color: '#0f172a' }}>{fmt(inv.subtotal)}</td>
                      <td style={{ padding: '10px 14px', color: '#64748b' }}>{fmtDate(inv.date_str)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Outstanding Invoices */}
          <div style={{ flex: 1, minWidth: 320, background: '#fff', borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,.06)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>
                <i className="fas fa-exclamation-circle me-2" style={{ color: '#ef4444' }}></i>Outstanding
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#ef4444' }}>{fmt(overview.outstanding)} due</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Invoice','Customer','Total','Remaining'].map(h => (
                      <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(outstandingInvoices || []).length === 0 ? (
                    <tr><td colSpan={4} style={{ padding: '30px', textAlign: 'center', color: '#16a34a' }}>
                      <i className="fas fa-check-circle me-2"></i>All invoices are paid!
                    </td></tr>
                  ) : (outstandingInvoices || []).map(inv => (
                    <tr key={inv.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ background: '#fee2e2', color: '#7c2d12', borderRadius: 5, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>{inv.number}</span>
                      </td>
                      <td style={{ padding: '10px 14px', color: '#374151', fontWeight: 600, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.customer_name || '—'}</td>
                      <td style={{ padding: '10px 14px', color: '#64748b' }}>{fmt(inv.subtotal)}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ background: '#fef2f2', color: '#ef4444', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>{fmt(inv.remainingAmount)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── Row 4: Top Customers ── */}
        <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,.06)', border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: 18 }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9' }}>
            <span style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>
              <i className="fas fa-trophy me-2" style={{ color: '#f59e0b' }}></i>Top Customers by Revenue
            </span>
          </div>
          {(topCustomers || []).length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No invoice data yet</div>
          ) : (
            <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {(topCustomers || []).map((c, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 26, height: 26, borderRadius: '50%', background: i < 3 ? '#fef3c7' : '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: i < 3 ? '#f59e0b' : '#94a3b8', flexShrink: 0 }}>
                    {i + 1}
                  </div>
                  <div style={{ width: 200, fontSize: 13, fontWeight: 600, color: '#374151', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                  <div style={{ flex: 1, height: 8, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.round((c.revenue / maxRevenue) * 100)}%`, background: i === 0 ? '#f59e0b' : i === 1 ? '#9ca3af' : i === 2 ? '#b45309' : '#2563eb', borderRadius: 99 }}></div>
                  </div>
                  <div style={{ width: 100, textAlign: 'right', fontSize: 13, fontWeight: 800, color: '#0f172a', flexShrink: 0 }}>{fmtK(c.revenue)}</div>
                  <div style={{ width: 60, textAlign: 'right', fontSize: 11, color: '#64748b', flexShrink: 0 }}>{c.count} inv.</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Row 5: Activity Summary ── */}
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 18 }}>
          {monthly.slice(-3).reverse().map((m, i) => (
            <div key={i} style={{ flex: 1, minWidth: 200, background: '#fff', borderRadius: 14, padding: '16px 18px', boxShadow: '0 1px 4px rgba(0,0,0,.06)', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: .4, marginBottom: 10 }}>{m.label}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 13, color: '#374151' }}><i className="fas fa-file-alt me-2" style={{ color: '#7c3aed' }}></i>Quotations</span>
                <span style={{ fontWeight: 700 }}>{m.quotes}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 13, color: '#374151' }}><i className="fas fa-file-invoice-dollar me-2" style={{ color: '#b45309' }}></i>Invoices</span>
                <span style={{ fontWeight: 700 }}>{m.invoices}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 13, color: '#374151' }}><i className="fas fa-rupee-sign me-2" style={{ color: '#b91c1c' }}></i>Revenue</span>
                <span style={{ fontWeight: 700 }}>{fmtK(m.revenue)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, color: '#374151' }}><i className="fas fa-hand-holding-usd me-2" style={{ color: '#0d9488' }}></i>Collected</span>
                <span style={{ fontWeight: 700, color: '#16a34a' }}>{fmtK(m.collected)}</span>
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}
